/* eslint-disable max-lines */
import type {
    Activities,
    ActivityData,
    ImportIdle,
    ImportSaving,
    ImportSelecting,
} from '@gaia/types';
import combineAsyncIterables from '@gaia/utils/combineAsyncIterables';
import fetchJSON from '@gaia/utils/fetchJSON';
import fetchPaginatedJSON from '@gaia/utils/fetchPaginatedJSON';
import getActivityObject from '@gaia/utils/gaia/getActivityObject';
import getParentFolderId from '@gaia/utils/getParentFolderId';
import {uniqSavedItems} from '@gaia/utils/uniqAsyncIterable';
import memoize from 'lodash/memoize';
import once from 'lodash/once';
import orderBy from 'lodash/orderBy';
import qs from 'qs';

import fetchActivities from './fetchActivities';

// There are two distinct codepaths for fetching remote saved items. The "quickly" implementation
// downloads all remote items and does filtering / sorting clientside. The "efficiently"
// implementation does does filtering / sorting serverside.

const MAX_REMOTE_ITEMS_FOR_QUICKLY = 5000;

export const SORT_KEYS = {
    distance: {
        humanName: 'Distance',
        remoteKey: 'distance',
        directionLabels: ['Shortest', 'Longest'],
        getSortValue: (item: SavedItem) => (item as any).distance || 0,
    },
    elevation: {
        humanName: 'Elevation',
        remoteKey: 'distance',
        directionLabels: ['Least Gain', 'Most Gain'],
        getSortValue: (item: SavedItem) => (item as any).total_ascent || (item as any).ascent || 0,
    },
    time_created: {
        humanName: 'Time Created',
        remoteKey: 'create_date',
        directionLabels: ['Oldest', 'Newest'],
        getSortValue: (item: SavedItem) => item.time_created && Date.parse(item.time_created),
    },
    title: {
        humanName: 'Title',
        remoteKey: 'name',
        directionLabels: ['A-Z', 'Z-A'],
        getSortValue: (item: SavedItem) => item.title && item.title.toLocaleLowerCase(),
    },
};

export type GetSavedItemsOptions = GetLocalSavedItemsOptions &
    GetRemoteSavedItemsOptions & {
        importState?: ImportIdle | ImportSelecting | ImportSaving;
        activeFolderId?: string;
    };

export function isItemVisible(item: SavedItem, options: GetSavedItemsOptions) {
    //Check visibility filter before displaying item
    const {showHiddenItems, showVisibleItems} = options.filter;

    // User is not able to adjust an items visibility until after it's been saved to the server and has an id
    // These items should always show if showVisibleItems is True
    if (!item.id) return showVisibleItems;
    const isVisible = options.visibilities[item.id] !== false;
    if ((isVisible && showVisibleItems) || (!isVisible && showHiddenItems)) return true;
    return false;
}

export function isTrackActivityMatch(
    item: SavedItem,
    activityCategories: string[],
    activitiesData?: ActivityData[]
) {
    // Check given track's activity matches activities filter
    if (activityCategories.length === 0 || !activitiesData) return false;
    if (
        item.type === 'track' &&
        item.track_type !== 'route' &&
        item.activities &&
        item.activities.length > 0
    ) {
        const track_activity = getActivityObject(item.activities, activitiesData);
        const categoryId = track_activity?.id.split('.')[0];
        return activityCategories.includes(categoryId || '');
    } else return false;
}

/**
 * Determines whether a saved item should be shown or hidden prior to passing it to prepareSavedItems.
 * Filters out archived items, items not in the current folder, and items that do not match the current visibility filter.
 * Does not filter out items that match the current search or item type filter, as those are determined in prepareSavedItems.
 */
export function showSavedItem({
    item,
    activeFolderId,
    options,
    itemFilterActive,
    isActivityFilterActive,
    activityData,
}: {
    item: SavedItem;
    activeFolderId?: string;
    options: GetSavedItemsOptions;
    itemFilterActive: boolean;
    isActivityFilterActive: boolean;
    activityData?: ActivityData[];
}) {
    const parentFolderId = getParentFolderId(item);

    if (item.archived || item.deleted) return false;

    // If there is an activeFolderId, we're inside a folder. An item should hide if:
    // - It is not a child of the active folder
    // - OR it is not visible
    // - OR it does not match the current activity filter
    if (activeFolderId) {
        if (parentFolderId !== activeFolderId) return false;

        const isVisible = isItemVisible(item, options);

        if (isActivityFilterActive) {
            return (
                isTrackActivityMatch(item, options.filter.activityCategories, activityData) &&
                isVisible
            );
        }
        return isVisible;
    }

    // We are in the root view if there is no activeFolderId. Search/filter operates globally here,
    // so if there is an active search or filter, we should return both filed and unfiled items.
    // If there is no active search or filter, hide all items that are filed into a folder
    if (!options.search && !itemFilterActive && !isActivityFilterActive) {
        if (parentFolderId) return false;
    }

    // Otherwise, there is a search or filter(s) applied. We should only filter items out if they
    // are not visible, or if the track activity filter is active
    if (isItemVisible(item, options)) {
        return isActivityFilterActive
            ? isTrackActivityMatch(item, options.filter.activityCategories, activityData)
            : true;
    }

    return false;
}

async function getIterator({
    importState,
    activeFolderId,
    ...options
}: GetSavedItemsOptions & {
    // by the time this is called, it has been narrowed to `ImportIdle | ImportSelecting | undefined`
    importState: ImportIdle | ImportSelecting | undefined;
}): Promise<[string, undefined] | [undefined, (SavedItem[] | AsyncIterable<SavedItem>)[]]> {
    let iterators: (SavedItem[] | AsyncIterable<SavedItem>)[];

    if (activeFolderId) {
        if (!options.folders[activeFolderId]) {
            return ['Folder not available', undefined];
        }

        let {
            areas = [],
            tracks = [],
            routes = [],
            waypoints = [],
            folders = [],
        } = options.folders[activeFolderId];

        // The items in a folder should be replaced with a local version, if present
        waypoints = waypoints.map((waypoint) => {
            const id = waypoint.id || waypoint.localId;
            const localWaypoint = id && options.waypoints[id]?.properties;
            return localWaypoint || waypoint;
        });
        areas = areas.map((area) => {
            const id = area.id || area.localId;
            const localArea = id && options.areas[id]?.properties;
            return localArea || area;
        });
        tracks = tracks.map((track) => {
            const id = track.id || track.localId;
            const localTrack = id && options.tracks[id]?.properties;
            return localTrack || track;
        });
        routes = routes.map((route) => {
            const id = route.id || route.localId;
            const localRoute = id && options.tracks[id]?.properties;
            return localRoute || route;
        });

        folders = folders.map((folder) => {
            const id = folder.id || folder.localId;
            const localFolder = id && options.folders[id];
            return {
                ...folder,
                ...localFolder,
            };
        });

        iterators = [
            prepareSavedItems([...areas, ...tracks, ...routes, ...waypoints, ...folders], options),
        ];
    } else if (importState && importState.status !== 'idle') {
        iterators = [await getPreparedImports(options)];
    } else {
        // @TODO `await` appears unnecessary
        iterators = [await getLocalSavedItems(options), await getRemoteSavedItems(options)];
    }

    return [undefined, iterators];
}

export default async function getSavedItems({
    importState,
    activeFolderId,
    ...options
}: GetSavedItemsOptions): Promise<
    [string, undefined] | [undefined, () => AsyncGenerator<SavedItem, void, unknown>]
> {
    // Don't trigger an update while an import is saving. This ensures we show a loader and
    // the interface doesn't flash as items get updated
    if (importState?.status === 'saving') return ['Saving', undefined];

    const sortValue = SORT_KEYS[options.sort.sortKey as SavedItemSortKeys].getSortValue;

    const [itemError, itemIterator] = await getIterator({
        importState,
        activeFolderId,
        ...options,
    });

    const {showWaypoints, showAreas, showRoutes, showTracks, showFolders} = options.filter;
    const itemFilterActive = ![showWaypoints, showAreas, showRoutes, showTracks, showFolders].every(
        (f) => f
    );

    // *Filter for activity after both remote and local tracks have been fetched & combined.

    // *Filtering by activity is not available server-side. When new changes to a tracks activity are saved locally and activity filter is applied, the local track will be evaluated and possibly filtered out. When remote and local tracks are then combined, the remote track (with old activity data) will return.

    const isActivityFilterActive = options.filter.activityCategories.length > 0;

    let activitiesData: Activities | undefined;
    if (isActivityFilterActive) {
        activitiesData = await fetchActivities();
    }

    if (itemError || !itemIterator) return ['Not ready', undefined];
    return [
        undefined,
        async function* () {
            // Yield all other items
            yield* filterAsyncIterable(
                uniqSavedItems(
                    combineAsyncIterables<SavedItem>(
                        sortValue,
                        options.sort.sortDirection,
                        ...itemIterator
                    )
                ),
                (item: SavedItem) =>
                    showSavedItem({
                        item,
                        activeFolderId,
                        options,
                        itemFilterActive,
                        isActivityFilterActive,
                        activityData: activitiesData?.activities,
                    })
            );
        },
    ];
}

interface GetLocalSavedItemsOptions extends PrepareSavedItemsOptions {
    waypoints: Record<string, Waypoint>;
    tracks: Record<string, Track>;
    areas: Record<string, Area>;
    folders: Record<string, Folder>;
}

function getLocalSavedItems(options: GetLocalSavedItemsOptions) {
    const waypoints = Object.values(options.waypoints)
        .filter((waypoint) => !waypoint.properties.trackLocalId)
        .map(({properties}) => properties);

    const tracks = Object.values(options.tracks)
        .filter((track) => !track.properties.isPublicTrack)
        .map(({properties}) => properties);

    const areas = Object.values(options.areas).map(({properties}) => properties);

    const folders = Object.values(options.folders || {}).map((folder) => ({
        ...folder,
        id: folder.id || folder.localId,
    }));

    return prepareSavedItems([...waypoints, ...tracks, ...areas, ...folders], options);
}

function getLatestImport(data: Record<string, Waypoint | Area | Track>) {
    return Object.values(data)
        .filter((item) => item.properties.isLatestImport && !item.properties.archived)
        .map(({properties}) => properties);
}

export function getPreparedImports(options: GetLocalSavedItemsOptions) {
    return prepareSavedItems(
        [
            ...getLatestImport(options.waypoints),
            ...getLatestImport(options.tracks),
            ...getLatestImport(options.areas),
        ],
        options
    );
}

const fetchSavedItemCount = once(async () => {
    const summary = await fetchJSON('/api/objects/');
    return (
        Number(summary.waypoints_count) +
        Number(summary.tracks_count) +
        Number(summary.routes_count) +
        Number(summary.areas_count)
    );
});

const getRemoteSavedItemsEfficiently = memoize(
    ({
        search,
        sort: {sortKey, sortDirection},
        filter,
        forceUpdateKey,
    }: GetRemoteSavedItemsOptions) => {
        const urlQuery = qs.stringify({
            sort_direction: sortDirection,
            sort_field: SORT_KEYS[sortKey as SavedItemSortKeys].remoteKey,
            search,
            show_filed: true,
            show_archived: false,
            forceUpdateKey,
        });

        return mapAsyncIterable<any, SavedItem>(
            combineAsyncIterables(
                SORT_KEYS[sortKey as SavedItemSortKeys].getSortValue,
                sortDirection,
                !filter.showWaypoints
                    ? []
                    : fetchPaginatedJSON(`/api/objects/waypoint/?${urlQuery}`),
                !filter.showTracks ? [] : fetchPaginatedJSON(`/api/objects/track/?${urlQuery}`),
                !filter.showRoutes
                    ? []
                    : mapAsyncIterable(
                          fetchPaginatedJSON(`/api/objects/route/?${urlQuery}`),
                          (item: any) => ({
                              ...item,
                              path: 'track',
                              track_type: 'route',
                          })
                      ),
                !filter.showAreas ? [] : fetchPaginatedJSON(`/api/objects/area/?${urlQuery}`),
                !filter.showFolders ? [] : fetchPaginatedJSON(`/api/objects/folder/?${urlQuery}`)
            ),
            ({path, ...item}) => ({...item, type: path})
        );
    }
);

const asyncIteratorToArray = memoize(
    async <T extends Record<string, any>>(iterable: AsyncIterable<T>): Promise<T[]> => {
        const array = [];
        for await (const item of iterable) {
            array.push(item);
        }
        return array;
    }
);

async function getRemoteSavedItemsQuickly(options: GetRemoteSavedItemsOptions) {
    return prepareSavedItems(
        await asyncIteratorToArray(
            getRemoteSavedItemsEfficiently({
                search: '',
                sort: {sortKey: 'time_created', sortDirection: 'asc'},
                filter: {
                    showWaypoints: true,
                    showTracks: true,
                    showAreas: true,
                    showRoutes: true,
                    showFolders: true,
                    showHiddenItems: true,
                    showVisibleItems: true,
                    activityCategories: [],
                },
                visibilities: {},
                forceUpdateKey: options.forceUpdateKey,
            })
        ),
        options
    );
}

interface PrepareSavedItemsOptions {
    search: string;
    visibilities: Record<string, boolean>;
    filter: FilterOptions;
    sort: SortOptions;
    forceUpdateKey?: string;
}

/**
 * Filter out saved items that do not correspond to the current search or item type filter
 * and order them according to the current sort.
 */
function prepareSavedItems(
    savedItems: SavedItem[],
    {search, sort: {sortKey, sortDirection}, filter}: PrepareSavedItemsOptions
) {
    return orderBy(
        savedItems
            .filter(
                (savedItem) =>
                    !search || savedItem.title?.toLowerCase().includes(search.toLowerCase())
            )
            .filter(
                (item) =>
                    (item.type === 'waypoint' && filter.showWaypoints) ||
                    (item.type === 'track' && item.track_type !== 'route' && filter.showTracks) ||
                    (item.type === 'track' && item.track_type === 'route' && filter.showRoutes) ||
                    (item.type === 'area' && filter.showAreas) ||
                    (item.type === 'folder' && filter.showFolders)
            ),
        SORT_KEYS[sortKey as SavedItemSortKeys].getSortValue,
        sortDirection
    );
}

type GetRemoteSavedItemsOptions = PrepareSavedItemsOptions;

export async function* getRemoteSavedItems(options: GetRemoteSavedItemsOptions) {
    const count = await fetchSavedItemCount();

    if (count > MAX_REMOTE_ITEMS_FOR_QUICKLY) {
        yield* getRemoteSavedItemsEfficiently(options);
    } else {
        yield* await getRemoteSavedItemsQuickly(options);
    }
}

export async function* mapAsyncIterable<T, U>(iterable: AsyncIterable<T>, mapper: (t: T) => U) {
    for await (const item of iterable) yield mapper(item);
}

export async function* filterAsyncIterable<T>(
    iterable: AsyncIterable<T>,
    filter: (t: T) => boolean
) {
    for await (const item of iterable) if (filter(item)) yield item;
}
